import { useLayoutEffect, useMemo } from "react";
import { createSearchParams, type SetURLSearchParams, useLocation, useMatches, useSearchParams } from "react-router";

type ParamsOptions = {
    bucket?: string;
};

type ReturnType = [URLSearchParams, SetURLSearchParams];

export function useSessionStorageSearchParams({ bucket }: ParamsOptions = {}): ReturnType {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchString = searchParams.toString();

    const routeKey = storageKey(useStableRouteKey(), bucket);
    const stored = read(routeKey);

    // The URL is the source of truth whenever it carries params. Fall back to the latest
    // stored value only when the URL is empty (e.g. initial load or a params-less navigation),
    // showing it immediately to avoid a re-render flash.
    const actualSearchParams = useMemo(() => {
        const preferSearchParamsFromStorage = searchString === "" && stored !== null && stored !== "";
        return preferSearchParamsFromStorage ? new URLSearchParams(stored) : searchParams;
    }, [searchParams, searchString, stored]);

    useLayoutEffect(() => {
        if (searchString !== "") {
            // URL is the latest intent — persist it and never revert edits.
            write(routeKey, searchParams);
            return;
        }

        // Empty URL — restore the latest stored value, if any.
        const latest = read(routeKey);
        if (latest !== null && latest !== "") {
            setSearchParams(latest, { replace: true, state: location.state });
        }
    }, [searchString, location, routeKey, searchParams, setSearchParams]);

    // Persist searchParams into sessionStorage
    const handleSetSearchParams: SetURLSearchParams = (nextInit, navigateOpts) => {
        const handler = (prev: URLSearchParams) => {
            const invoked = typeof nextInit === "function" ? nextInit(prev) : nextInit;
            const params = createSearchParams(invoked ?? "");
            write(routeKey, params);

            return params;
        };

        setSearchParams(handler, navigateOpts);
    };

    return [actualSearchParams, handleSetSearchParams];
}

function useStableRouteKey(): string {
    const matches = useMatches();

    return useMemo(() => matches[matches.length - 1]?.id ?? "", [matches]);
}

function storageKey(routeKey: string, bucket?: string) {
    return bucket !== undefined && bucket !== "" ? `params:${routeKey}:${bucket}` : `params:${routeKey}`;
}

function read(routeKey: string) {
    return sessionStorage.getItem(routeKey);
}

function write(routeKey: string, params: URLSearchParams) {
    sessionStorage.setItem(routeKey, params.toString());
}
